import { gql } from "@apollo/client";
import client from "../apolloClient";
import { Role, CreateRoleRequest } from "../../schemas/roleSchema";

export interface RolesResponse {
  data: Role[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface RoleFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface UpdateRolePayload {
  id: string;
  role: Partial<CreateRoleRequest>;
}

const GET_ALL_ROLES = gql`
  query Roles($page: Int, $pageSize: Int, $search: String) {
    roles(page: $page, pageSize: $pageSize, search: $search) {
      roles {
        id
        name
        description
        tenantId
        permissions {
          id
          action
        }
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_ROLE_BY_ID = gql`
  query Role($id: ID!) {
    role(id: $id) {
      id
      name
      description
      tenantId
      permissions {
        id
        action
      }
    }
  }
`;

const CREATE_ROLE = gql`
  mutation CreateRole($input: CreateRoleInput!) {
    createRole(input: $input)
  }
`;

const UPDATE_ROLE = gql`
  mutation UpdateRole($id: ID!, $input: UpdateRoleInput!) {
    updateRole(id: $id, input: $input)
  }
`;

const DELETE_ROLES = gql`
  mutation DeleteRoles($ids: [ID!]!) {
    deleteRoles(ids: $ids)
  }
`;

export const roleService = {
  getAll: async (filters: RoleFilters = {}): Promise<RolesResponse> => {
    const { data } = await client.query({
      query: GET_ALL_ROLES,
      variables: filters,
      fetchPolicy: "network-only",
    });

    const result = data.roles;
    return {
      data: result.roles || [],
      page: result.page,
      pageSize: result.pageSize,
      total: result.total,
      totalPages: result.totalPages,
      hasNextPage: result.hasNextPage,
      hasPrevPage: result.hasPrevPage,
    };
  },

  getById: async (id: string): Promise<Role> => {
    const { data } = await client.query({
      query: GET_ROLE_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.role;
  },

  create: async (input: CreateRoleRequest): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_ROLE,
      variables: { input },
    });
    return data.createRole;
  },

  update: async (payload: UpdateRolePayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_ROLE,
      variables: {
        id: payload.id,
        input: payload.role,
      },
    });
    return data.updateRole;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_ROLES,
      variables: { ids },
    });
    return data.deleteRoles;
  },
};
